import React, { useState } from "react";
import { api, errMsg, fmtINR, fmtDate, fmtDateTime } from "@/lib/api";
import { Money, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const CHUNK = 1.5 * 1024 * 1024;

const uploadInChunks = async (file, onProgress) => {
  const { data } = await api.post("/backup/upload/start");
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", data.upload_id);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await api.post("/backup/upload/chunk", fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return data.upload_id;
};

const PreviewCard = ({ pv }) => (
  <Card className="rounded-lg" data-testid="restore-preview">
    <CardContent className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold">Backup preview — {fmtDate(pv.meta.date_from)} → {fmtDate(pv.meta.date_to)}</h3>
          <p className="text-xs text-muted-foreground">Created {fmtDateTime(pv.meta.created_at)} by {pv.meta.created_by} · total active bills <Money paise={pv.total_bill_paise} className="text-foreground" /></p>
        </div>
        <StatusBadge status="unreviewed" label="Preview only — nothing changed yet" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded border overflow-hidden">
          <table className="w-full text-sm" data-testid="restore-counts">
            <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide"><tr>
              <th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">In file</th><th className="text-right px-3 py-2">Currently in app (will be replaced)</th>
            </tr></thead>
            <tbody>
              {Object.entries(pv.labels).map(([k, label]) => (
                <tr key={k} className="border-t" data-testid={`restore-count-${k}`}>
                  <td className="px-3 py-1.5">{label}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{pv.file_counts[k] ?? 0}</td>
                  <td className={`px-3 py-1.5 text-right font-mono-num ${pv.existing_counts[k] ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>{pv.existing_counts[k] ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded border overflow-hidden max-h-[320px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="restore-days">
            <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide"><tr>
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Store</th><th className="text-right px-3 py-2">Bills</th><th className="text-right px-3 py-2">Bill total</th><th className="text-right px-3 py-2">Cash</th><th className="px-3 py-2">Status</th>
            </tr></thead>
            <tbody>
              {pv.days.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-center text-muted-foreground text-xs">No day data in this file</td></tr>}
              {pv.days.map((d) => (
                <tr key={`${d.business_date}-${d.store_id}`} className="border-t">
                  <td className="px-3 py-1.5 font-mono-num text-xs">{fmtDate(d.business_date)}</td>
                  <td className="px-3 py-1.5 text-xs">{d.store_name}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{d.bills}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{fmtINR(d.bill_total_paise)}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{fmtINR(d.cash_paise)}</td>
                  <td className="px-3 py-1.5">{d.status ? <StatusBadge status={d.status === "finalized" ? "finalized" : "open-day"} /> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Sample bills from the file</p>
        <div className="rounded border overflow-x-auto">
          <table className="w-full text-sm" data-testid="restore-sample-bills">
            <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide"><tr>
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Store</th><th className="text-left px-3 py-2">Bill No</th><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Cashier</th><th className="text-right px-3 py-2">Amount</th>
            </tr></thead>
            <tbody>
              {pv.sample_bills.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-center text-muted-foreground text-xs">No bills in this file</td></tr>}
              {pv.sample_bills.map((b, i) => (
                <tr key={i} className={`border-t ${b.status === "void" ? "opacity-50" : ""}`}>
                  <td className="px-3 py-1.5 font-mono-num text-xs">{fmtDate(b.business_date)}</td>
                  <td className="px-3 py-1.5 text-xs">{b.store_name}</td>
                  <td className="px-3 py-1.5 font-mono-num font-semibold">{b.bill_no}</td>
                  <td className="px-3 py-1.5 text-xs">{b.customer_name || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{b.cashier_name}</td>
                  <td className="px-3 py-1.5 text-right font-mono-num">{fmtINR(b.amount_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {Object.values(pv.masters_missing).some((n) => n > 0) && (
        <p className="text-xs text-muted-foreground" data-testid="restore-masters-note">
          Master records missing here that will be added: {Object.entries(pv.masters_missing).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(", ")}
        </p>
      )}
    </CardContent>
  </Card>
);

export default function RestoreTab() {
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setResult(null); setPreview(null); setProgress(0);
    try {
      const uploadId = await uploadInChunks(file, setProgress);
      const { data } = await api.post("/backup/preview", { upload_id: uploadId });
      setPreview(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setProgress(null); }
  };

  const cancel = async () => {
    if (preview) api.delete(`/backup/upload/${preview.upload_id}`).catch(() => {});
    setPreview(null);
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backup/restore", { upload_id: preview.upload_id, approve: true });
      setResult(data); setPreview(null); setConfirmOpen(false);
      toast.success("Backup restored — data for the range replaced");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="restore-tab">
      <Card className="rounded-lg"><CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded bg-[hsl(var(--info))]/15 flex items-center justify-center shrink-0"><Upload className="h-4 w-4 text-[hsl(var(--info))]" /></div>
          <div>
            <h2 className="text-base md:text-lg font-semibold">Restore from an encrypted backup</h2>
            <p className="text-sm text-muted-foreground">Upload a <span className="font-mono-num">.rkb</span> file. Rokadly decrypts it and shows a preview first — nothing changes until you click <b>Approve</b>. On approval, existing records inside the backup's date range are replaced by the file's data.</p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-input bg-secondary/40 hover:border-[hsl(var(--brass))] cursor-pointer text-sm font-medium transition-colors duration-150" data-testid="restore-file-label">
          <Upload className="h-3.5 w-3.5" />Choose backup file
          <input type="file" accept=".rkb,application/octet-stream" onChange={onFile} className="hidden" data-testid="restore-file-input" disabled={progress !== null} />
        </label>
        {progress !== null && (
          <div className="space-y-1" data-testid="restore-progress">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">{progress < 100 ? `Uploading… ${progress}%` : "Decrypting and building preview…"}</p>
          </div>
        )}
      </CardContent></Card>

      {preview && (<>
        <PreviewCard pv={preview} />
        <Alert className="border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10" data-testid="restore-warning">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
          <AlertTitle>Approving replaces data</AlertTitle>
          <AlertDescription className="text-sm">All current records dated {fmtDate(preview.meta.date_from)} → {fmtDate(preview.meta.date_to)} (every store) will be removed and substituted with this file's data. Days outside the range are untouched.</AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cancel} data-testid="restore-cancel-button"><X className="h-3.5 w-3.5 mr-1.5" />Cancel</Button>
          <Button onClick={() => setConfirmOpen(true)} className="bg-[hsl(var(--emerald))] hover:bg-[hsl(var(--emerald))]/90 text-white" data-testid="restore-approve-button">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve &amp; replace app data
          </Button>
        </div>
      </>)}

      {result && (
        <Alert className="border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/10" data-testid="restore-result">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
          <AlertTitle>Restored {fmtDate(result.date_from)} → {fmtDate(result.date_to)}</AlertTitle>
          <AlertDescription className="text-sm">
            Inserted: {Object.entries(result.inserted).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(", ") || "nothing"} · removed: {Object.values(result.removed).reduce((a, b) => a + b, 0)} old records
            {Object.values(result.masters_added).some((n) => n) && <> · masters added: {Object.entries(result.masters_added).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(", ")}</>}
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approve restore?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This substitutes the app's data for {preview && `${fmtDate(preview.meta.date_from)} → ${fmtDate(preview.meta.date_to)}`} with the backup's contents. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Back</Button>
            <Button onClick={approve} disabled={busy} className="bg-[hsl(var(--emerald))] hover:bg-[hsl(var(--emerald))]/90 text-white" data-testid="restore-confirm-button">{busy ? "Restoring…" : "Yes, approve"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
